import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const slideshowData = [
  {
    image: '/images/slideshow/thugtv-messenger.png',
    caption: 'THUG.TV Messenger - Real-time messaging application with WebRTC voice & video, CRDT-based synchronization, and Web3 wallet integration'
  },
  {
    image: '/images/slideshow/dpln-ai-3d-editor.png',
    caption: 'DPLN.AI - Advanced 3D model generation and editing platform with AI-powered text-to-3D conversion, texture generation, and real-time preview'
  },
  {
    image: '/images/slideshow/porsche-911.gif',
    caption: 'Hyper-realistic Porsche 911 with launch control and responsive FMOD sound design for PINKSLIP CITY'
  },
  {
    image: '/images/slideshow/thugtv-metaverse-web.gif',
    caption: 'Unreleased fully interactive and immersive original web design with propriety chat and friends system for THUG.TV METAVERSE'
  },
  {
    image: '/images/slideshow/unreal-ui-overlay.gif',
    caption: 'Early work on web-based interactive Unreal Engine UI overlay and building system for THUG.TV METAVERSE'
  },
  {
    image: '/images/slideshow/alongside-commercial.jpg',
    caption: 'Immersive live production T.V. commercial with ALONGSIDE/ GLOBAL, provided environmental design and creative direction'
  },
  {
    image: '/images/slideshow/toxic-twilights.gif',
    caption: 'TOXIC TWILIGHTS, developed as Technical Director for Sombra Networks entirely from the ground up'
  },
  {
    image: '/images/slideshow/inferno-room.gif',
    caption: 'Created INFERNO ROOM on Second Life, including custom 3D mesh, texturing and code.'
  },
  {
    image: '/images/slideshow/metahuman-character.gif',
    caption: 'Fully dynamic, rigged and animated metahuman character design for THUG.TV METAVERSE'
  },
  {
    image: '/images/slideshow/lock-picking-system.gif',
    caption: 'Proprietary lock picking system on Unreal Engine commissioned for Steam title SPECTRUM'
  },
  {
    image: '/images/slideshow/helix-metaverse.gif',
    caption: 'Original environment design, traffic/pedestrian A.I. (with working traffic lights), speedometer, vehicles and more for HELIX METAVERSE'
  },
  {
    image: '/images/slideshow/mortarmen-ragdoll.gif',
    caption: 'Slope and velocity-based ragdoll, fall damage and feign death system designed in C++ for MORTARMEN'
  },
  {
    image: '/images/slideshow/embers-park.gif',
    caption: 'Previsualization environment for EMBERS PARK displaying runtime Unreal Engine environment on flat-surface in-game walls preparing for real-world LED walls'
  },
  {
    image: '/images/slideshow/shotgun-samurai.gif',
    caption: 'Entire design programmed, 3d modeled and animated with original sound as requested for SHOTGUN SAMURAI'
  },
  {
    image: '/images/slideshow/edge-climbing-system.gif',
    caption: 'Runtime-dynamic custom edge climbing system with replication for TOXIC TWILIGHTS, players can climb any edge deemed mathematically climbable'
  },
  {
    image: '/images/slideshow/niftyville-ai.gif',
    caption: 'GTA-inspired area-based dynamic AI system with multiplayer replication designed for NIFTYVILLE'
  },
  {
    image: '/images/slideshow/shores-of-plunder-ai.gif',
    caption: 'Custom artificial intelligence design for SHORES OF PLUNDER with player interactions, triggers, animations, health and combat system'
  },
  {
    image: '/images/slideshow/apeing-ai-pumpfun-clone.png',
    caption: 'PUMP.FUN clone for APEING.AI / STREAM.FUN featuring Kick & Amazon IVS live streaming integration, TradingView charts, real-time trading, and Phoenix WebSocket chat'
  }
]

const Slideshow = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)

  // Color palette for each slide - using CSS variable names
  const slideColors = [
    'primary',    // Blue
    'secondary',  // Purple
    'accent',     // Cyan
    'info',       // Light Blue
    'success',    // Green
    'warning',    // Orange
    'error',      // Red
    'primary',    // Blue (repeat)
    'secondary',  // Purple (repeat)
    'accent',     // Cyan (repeat)
    'info',       // Light Blue (repeat)
    'success',    // Green (repeat)
    'warning',    // Orange (repeat)
    'error',      // Red (repeat)
    'primary',    // Blue (repeat)
  ]

  const getSlideColor = (index) => slideColors[index % slideColors.length]

  // Get CSS variable color value
  const getColorValue = (colorName) => {
    const root = document.documentElement
    const colorVar = getComputedStyle(root).getPropertyValue(`--${colorName}`)
    if (colorVar) {
      return colorVar.trim()
    }
    // Fallback colors if CSS variables aren't available
    const fallbacks = {
      primary: '#3b82f6',
      secondary: '#8b5cf6',
      accent: '#06b6d4',
      info: '#0ea5e9',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
    }
    return fallbacks[colorName] || fallbacks.primary
  }

  // Auto-advance slideshow
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % slideshowData.length)
      }, 5000) // Change slide every 5 seconds
      return () => clearInterval(interval)
    }
  }, [isHovered])

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + slideshowData.length) % slideshowData.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slideshowData.length)
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  const getPreviousIndex = () => (currentIndex - 1 + slideshowData.length) % slideshowData.length
  const getNextIndex = () => (currentIndex + 1) % slideshowData.length

  const [hoveredImage, setHoveredImage] = useState(null)

  return (
    <div 
      className="relative w-full bg-base-100 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Three Images Side by Side */}
      <div className="flex w-full relative" style={{ height: 'clamp(250px, 30vh, 400px)' }}>
        {/* Previous Slide - Left */}
        <motion.div 
          className="relative w-1/3 cursor-pointer overflow-hidden"
          onClick={goToPrevious}
          style={{ height: '100%' }}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => setHoveredImage('prev')}
          onMouseLeave={() => setHoveredImage(null)}
        >
          <div className="relative w-full h-full">
            <img
              src={slideshowData[getPreviousIndex()].image}
              alt={slideshowData[getPreviousIndex()].caption}
              className="w-full h-full object-cover transition-all duration-500"
              style={{ 
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'brightness(0.6) saturate(0.8)'
              }}
            />
            <div 
              className={`absolute inset-0 transition-opacity duration-300 ${hoveredImage === 'prev' ? 'opacity-100' : 'opacity-0'}`}
              style={{
                background: `linear-gradient(to right, ${getColorValue(getSlideColor(getPreviousIndex()))}33, transparent)`
              }}
            />
          </div>
          <div className={`absolute inset-0 flex items-center justify-start pl-4 transition-opacity duration-300 z-20 ${hoveredImage === 'prev' ? 'opacity-100' : 'opacity-0'}`}>
            <div className="glass rounded-full p-2 glow-effect">
              <ChevronLeft className="w-6 h-6" style={{ color: getColorValue(getSlideColor(getPreviousIndex())) }} />
            </div>
          </div>
          {/* Caption Overlay - Shows on hover */}
          <motion.div 
            className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 z-10 ${hoveredImage === 'prev' ? 'opacity-100' : 'opacity-0'}`}
            initial={{ y: 20 }}
            animate={{ y: hoveredImage === 'prev' ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background with blur and gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/85 to-black/60 backdrop-blur-md" />
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to top, ${getColorValue(getSlideColor(getPreviousIndex()))}33, transparent)`
              }}
            />
            
            {/* Text Content */}
            <div className="relative px-8 py-10">
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: hoveredImage === 'prev' ? 1 : 0, y: hoveredImage === 'prev' ? 0 : 10 }}
                transition={{ duration: 0.3 }}
                className="text-white text-2xl font-bold text-center leading-relaxed drop-shadow-lg"
                style={{
                  textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 0 24px rgba(0, 0, 0, 0.6)'
                }}
              >
                {slideshowData[getPreviousIndex()].caption}
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Current Slide - Center */}
        <div 
          className="relative w-1/3 overflow-hidden"
          style={{ height: '100%' }}
          onMouseEnter={() => setHoveredImage('current')}
          onMouseLeave={() => setHoveredImage(null)}
        >
          <div className="relative w-full h-full">
            <AnimatePresence initial={false}>
              <motion.img
                key={currentIndex}
                src={slideshowData[currentIndex].image}
                alt={slideshowData[currentIndex].caption}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: 'easeInOut' }}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ 
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </AnimatePresence>
            <div 
              className={`absolute inset-0 transition-opacity duration-300 ${hoveredImage === 'current' ? 'opacity-100' : 'opacity-0'}`}
              style={{
                background: `linear-gradient(to bottom right, ${getColorValue(getSlideColor(currentIndex))}33, transparent, ${getColorValue(getSlideColor(currentIndex))}1a)`
              }}
            />
          </div>
          {/* Caption Overlay - Shows on hover */}
          <motion.div 
            className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 z-10 ${hoveredImage === 'current' ? 'opacity-100' : 'opacity-0'}`}
            initial={{ y: 20 }}
            animate={{ y: hoveredImage === 'current' ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background with blur and gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/85 to-black/60 backdrop-blur-md" />
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to top, ${getColorValue(getSlideColor(currentIndex))}33, transparent)`
              }}
            />
            
            {/* Text Content */}
            <div className="relative px-8 py-10">
              <motion.p 
                key={currentIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: hoveredImage === 'current' ? 1 : 0, y: hoveredImage === 'current' ? 0 : 10 }}
                transition={{ duration: 0.3 }}
                className="text-white text-2xl font-bold text-center leading-relaxed drop-shadow-lg"
                style={{
                  textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 0 24px rgba(0, 0, 0, 0.6)'
                }}
              >
                {slideshowData[currentIndex].caption}
              </motion.p>
            </div>
          </motion.div>
        </div>

        {/* Next Slide - Right */}
        <motion.div 
          className="relative w-1/3 cursor-pointer overflow-hidden"
          onClick={goToNext}
          style={{ height: '100%' }}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => setHoveredImage('next')}
          onMouseLeave={() => setHoveredImage(null)}
        >
          <div className="relative w-full h-full">
            <img
              src={slideshowData[getNextIndex()].image}
              alt={slideshowData[getNextIndex()].caption}
              className="w-full h-full object-cover transition-all duration-500"
              style={{ 
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'brightness(0.6) saturate(0.8)'
              }}
            />
            <div 
              className={`absolute inset-0 transition-opacity duration-300 ${hoveredImage === 'next' ? 'opacity-100' : 'opacity-0'}`}
              style={{
                background: `linear-gradient(to left, ${getColorValue(getSlideColor(getNextIndex()))}33, transparent)`
              }}
            />
          </div>
          <div className={`absolute inset-0 flex items-center justify-end pr-4 transition-opacity duration-300 z-20 ${hoveredImage === 'next' ? 'opacity-100' : 'opacity-0'}`}>
            <div className="glass rounded-full p-2 glow-effect">
              <ChevronRight className="w-6 h-6" style={{ color: getColorValue(getSlideColor(getNextIndex())) }} />
            </div>
          </div>
          {/* Caption Overlay - Shows on hover */}
          <motion.div 
            className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 z-10 ${hoveredImage === 'next' ? 'opacity-100' : 'opacity-0'}`}
            initial={{ y: 20 }}
            animate={{ y: hoveredImage === 'next' ? 0 : 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background with blur and gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/85 to-black/60 backdrop-blur-md" />
            <div 
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to top, ${getColorValue(getSlideColor(getNextIndex()))}33, transparent)`
              }}
            />
            
            {/* Text Content */}
            <div className="relative px-8 py-10">
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: hoveredImage === 'next' ? 1 : 0, y: hoveredImage === 'next' ? 0 : 10 }}
                transition={{ duration: 0.3 }}
                className="text-white text-2xl font-bold text-center leading-relaxed drop-shadow-lg"
                style={{
                  textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 0 24px rgba(0, 0, 0, 0.6)'
                }}
              >
                {slideshowData[getNextIndex()].caption}
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Dots Indicator - Always visible */}
      <div className="flex justify-center gap-2 py-6 bg-base-200/50 backdrop-blur-sm">
        {slideshowData.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-primary w-8 shadow-lg shadow-primary/50' 
                : 'bg-base-content/30 hover:bg-primary/50 w-2'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export default Slideshow
